import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Quote, BookOpen } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import BookCover from "@/components/BookCover";
import QuickLook from "@/components/QuickLook";
import { BOOKS, LANGUAGES } from "@/data/books";
import { trackClick } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

function BookCard({ book, lang, index, onPeek }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: EASE }}
      className="group flex flex-col"
      data-testid={`book-card-${index + 1}`}
    >
      <div className="relative">
        <div
          className="pointer-events-none absolute -inset-3 rounded-lg opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(closest-side, rgba(212,175,55,0.25), transparent 78%)",
          }}
          aria-hidden="true"
        />
        <button
          onClick={() => onPeek(book)}
          data-testid={`quick-look-btn-${index + 1}`}
          aria-label={`Peek inside ${book.title}`}
          className="relative block w-full cursor-pointer text-left transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:rotate-[0.6deg]"
        >
          <BookCover
            book={book}
            title={book.titles[lang]}
            className="shadow-card group-hover:shadow-lift"
          />
          <span className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-gold/40 bg-emerald-ink/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
            Peek Inside
          </span>
        </button>
        {book.debut && (
          <span className="absolute -right-2 top-4 z-10 rotate-3 rounded-sm bg-gold px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-ink shadow-card">
            Debut Title
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col pt-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald/60">
          Panchatantra Tales Series · Vol. {book.roman}
        </p>
        <div className="mt-2 min-h-[2.5rem]">
          <AnimatePresence mode="wait">
            <motion.h3
              key={lang}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="font-serif text-2xl font-semibold leading-snug text-charcoal"
              data-testid={`book-title-${index + 1}`}
            >
              {book.titles[lang]}
            </motion.h3>
          </AnimatePresence>
        </div>
        <p className="mt-1 text-sm font-medium text-emerald">{book.subtitle}</p>

        <p className="mt-3 flex gap-2 text-sm italic leading-relaxed text-charcoal/65">
          <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 rotate-180 text-gold" aria-hidden="true" />
          {book.morals[lang]}
        </p>

        <div
          className="mt-4 flex items-center gap-2 rounded-sm border border-emerald/15 bg-emerald/[0.04] px-3 py-2"
          data-testid={`book-languages-${index + 1}`}
        >
          <span className="text-sm leading-none" aria-hidden="true">
            🇺🇸 🇪🇸 🇩🇪 🇫🇷 🇮🇹 🇵🇹
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald/80">
            Available in 6 languages
          </span>
        </div>

        <a
          href={book.amazon}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`buy-amazon-btn-${index + 1}`}
          onClick={() => trackClick(`buy-${book.id}`, book.amazon, "library")}
          className="mt-5 flex items-center justify-center gap-2 rounded-full bg-emerald px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-cream transition-all duration-300 hover:bg-emerald-deep hover:shadow-card"
        >
          <ShoppingBag className="h-4 w-4 text-gold" />
          Buy on Amazon
        </a>
      </div>
    </motion.article>
  );
}

export default function Library() {
  const [lang, setLang] = useState("EN");
  const [peek, setPeek] = useState(null);

  return (
    <section
      id="library"
      data-testid="library-section"
      className="relative bg-cream py-24 text-charcoal lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            testId="library-heading"
            eyebrow="The Library"
            title={
              <>
                Six Fables.{" "}
                <em className="text-emerald">One Timeless Forest.</em>
              </>
            }
            sub="Every volume pairs a 2,000-year-old moral with cinematic, gallery-grade artwork — printed editions shipping worldwide through Amazon. Click any cover to peek inside."
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
            className="shrink-0"
          >
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-charcoal/55">
              Read the series in
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="tablist"
              aria-label="Language selector"
              data-testid="language-switcher"
            >
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  role="tab"
                  aria-selected={lang === l.code}
                  data-testid={`lang-tab-${l.code.toLowerCase()}`}
                  onClick={() => setLang(l.code)}
                  className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-all duration-300 ${
                    lang === l.code
                      ? "border-emerald bg-emerald text-cream shadow-card"
                      : "border-charcoal/15 bg-white/60 text-charcoal/70 hover:border-gold hover:text-emerald"
                  }`}
                >
                  <span aria-hidden="true">{l.flag}</span>
                  {l.code}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-16 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {BOOKS.map((book, i) => (
            <BookCard key={book.id} book={book} lang={lang} index={i} onPeek={setPeek} />
          ))}
        </div>
      </div>

      <QuickLook book={peek} onClose={() => setPeek(null)} />
    </section>
  );
}
